import { FormExternalApi, formsExternalApi } from './api';
import {
  isPaymentSocketConfigured,
  isPaymentSocketConnected,
  startTransactionOverSocket,
} from './paymentSocket';

export type FieldMappings = NonNullable<FormExternalApi['fieldMappings']>;

/**
 * Build the `fields` object sent to core-v1 start-transaction (ConfirmDto.data.fields)
 * from form input values + external API field mappings.
 * Shared by WebSocket and HTTP entry points.
 */
export function buildStartTransactionFields(
  formValues: Record<string, unknown>,
  fieldMappings?: FieldMappings | null,
): Record<string, unknown> {
  const safeFormValues = formValues && typeof formValues === 'object' ? formValues : {};
  const fields: Record<string, unknown> = {};

  if (fieldMappings?.simpleFields && Array.isArray(fieldMappings.simpleFields)) {
    for (const mapping of fieldMappings.simpleFields) {
      if (mapping?.paramName == null || mapping?.inputName == null) continue;
      const value = safeFormValues[mapping.inputName];
      if (value !== undefined && value !== null) {
        fields[mapping.paramName] = value;
      }
    }
  }

  if (fieldMappings?.arrayFields && Array.isArray(fieldMappings.arrayFields)) {
    for (const arrayMapping of fieldMappings.arrayFields) {
      if (!arrayMapping?.paramName || !Array.isArray(arrayMapping.inputMappings)) continue;
      const arrayData: Record<string, unknown> = {};
      let hasData = false;
      for (const inputMapping of arrayMapping.inputMappings) {
        if (inputMapping?.key == null || inputMapping?.inputName == null) continue;
        const value = safeFormValues[inputMapping.inputName];
        if (value !== undefined && value !== null) {
          arrayData[inputMapping.key] = value;
          hasData = true;
        }
      }
      if (hasData) {
        fields[arrayMapping.paramName] = arrayData;
      }
    }
  }

  return fields;
}

export type StartTransactionParams = {
  formId: number;
  transactionId: string;
  formValues: Record<string, unknown>;
  /** Required for WebSocket path (mapping done on FE). HTTP path still maps on forms backend. */
  fieldMappings?: FieldMappings | null;
  /**
   * Prefer WebSocket → core-v1 when socket URL is configured.
   * Falls back to HTTP → forms constructor `/forms/:id/start-transaction` unless requireSocket.
   */
  preferSocket?: boolean;
  /**
   * When true, use socket only — no HTTP fallback.
   * Prefer leaving false so offline/unconnected published forms use HTTP start-transaction.
   */
  requireSocket?: boolean;
  /** Stable key for a single in-flight button press streak (dedupe rapid clicks). */
  idempotencyKey?: string;
};

/**
 * Shared start-transaction entry with two backend transports:
 * 1) WebSocket (core-v1 `payment_page_event` / START_TRANSACTION) when connected
 * 2) HTTP (forms constructor `POST /forms/:id/start-transaction` → external API)
 *
 * When the socket is not connected (or RPC fails and requireSocket is false),
 * falls back to the default HTTP path.
 */
export async function startTransaction(params: StartTransactionParams): Promise<unknown> {
  const {
    formId,
    transactionId,
    formValues,
    fieldMappings,
    preferSocket = true,
    requireSocket = false,
    idempotencyKey,
  } = params;

  const socketReady = isPaymentSocketConfigured() && isPaymentSocketConnected();

  if (requireSocket || (preferSocket && isPaymentSocketConfigured())) {
    if (requireSocket && !isPaymentSocketConfigured()) {
      throw new Error('Payment socket is not configured (REACT_APP_PAYMENT_SOCKET_URL)');
    }

    // Prefer socket only when already connected — otherwise use HTTP immediately
    // (avoid waiting for connect timeout on published forms that fell back to by-hash-id).
    if (socketReady || requireSocket) {
      if (!isPaymentSocketConfigured()) {
        throw new Error('Payment socket is not configured (REACT_APP_PAYMENT_SOCKET_URL)');
      }
      const fields = buildStartTransactionFields(formValues, fieldMappings);
      try {
        return await startTransactionOverSocket(transactionId, fields, transactionId);
      } catch (err) {
        if (requireSocket) throw err;
        console.warn(
          '[startTransaction] WebSocket failed, falling back to HTTP:',
          err instanceof Error ? err.message : err,
        );
      }
    } else {
      console.warn(
        '[startTransaction] Socket not connected; using HTTP start-transaction',
      );
    }
  }

  return formsExternalApi.startTransaction(formId, transactionId, formValues, idempotencyKey);
}
